import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrl, buildAuthHeadersForFetch } from "./RestService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IVendorModel from "../models/IVendorModel";
import ICart from "../models/ICart";

interface CreateCartFromOrderOptions {
    orderGuid: string;
    clearExistingItems?: boolean;
    skipUnavailableItems?: boolean;
}

interface CreateCartFromOrderResponse {
    cart: ICart;
}

interface GetCartResponse {
    cart: ICart;
}

/**
 * Create a cart from an existing order (reorder functionality)
 * Copies items from the specified order into the user's cart
 */
const createCartFromOrder = async (
    options: CreateCartFromOrderOptions,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<CreateCartFromOrderResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.CartCreateFromOrder,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(options),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || response.statusText);
    }

    return response.json();
};

/**
 * Get the current cart for the user
 */
const getCart = async (
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel,
    signal?: AbortSignal
): Promise<GetCartResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.CartGetCart,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
        signal,
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || response.statusText);
    }

    return response.json();
};

const CartService = {
    createCartFromOrder,
    getCart,
};

export default CartService;
